import React from 'react';
import { inr, CalcLabel, Stepper, OptionCard } from './shared';

const FOOD_NOTES = {
  yes: 'In-house catering is available at this venue.',
  optional: 'In-house catering is optional at this venue.',
  mandatory: 'In-house catering is mandatory with venue booking.',
  external_allowed: 'External caterers are allowed — or choose an in-house package below.'
};

export const StepFood = ({ config, selections, setSelections }) => {
  const food = config.food || {};
  const packages = food.packages || [];
  const sel = selections.food || {};
  const pick = (id) => setSelections({
    ...selections,
    food: sel.package_id === id ? null : { package_id: id, veg_guests: sel.veg_guests || 0, nonveg_guests: sel.nonveg_guests || 0, children: sel.children || 0, units: 1 }
  });
  const patch = (changes) => setSelections({ ...selections, food: { ...sel, ...changes } });
  const activePkg = packages.find(p => p.id === sel.package_id);

  return (
    <div className="space-y-3" data-testid="calc-step-food">
      <p className="text-xs text-[#1A3626]/70 bg-[#F3EFEB] border border-[#E5DCCF] px-4 py-3">{FOOD_NOTES[food.availability] || FOOD_NOTES.optional}</p>
      {food.availability !== 'mandatory' && (
        <OptionCard selected={!sel.package_id} onClick={() => setSelections({ ...selections, food: null })} testId="calc-food-none">
          <p className="font-wvc-display text-lg text-[#0A1710]">No food package</p>
          <p className="text-xs text-[#1A3626]/60 mt-1 font-light">Skip in-house catering for now.</p>
        </OptionCard>
      )}
      {packages.map(fp => (
        <OptionCard key={fp.id} selected={sel.package_id === fp.id} onClick={() => pick(fp.id)} testId={`calc-food-${fp.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-wvc-display text-lg text-[#0A1710]">{fp.name}</p>
              <p className="text-xs text-[#1A3626]/60 mt-0.5 font-light">
                {(fp.meals_included || []).join(' · ')}
                {fp.menu_items_count ? ` · ${fp.menu_items_count} menu items` : ''}
                {fp.live_counters ? ` · ${fp.live_counters} live counters` : ''}
              </p>
              {(fp.cuisines || []).length > 0 && <p className="text-[11px] text-[#C6A87C] mt-1 uppercase tracking-[0.12em]">{fp.cuisines.join(' · ')}</p>}
            </div>
            <div className="text-right shrink-0">
              {fp.pricing_mode === 'fixed'
                ? <p className="font-wvc-display text-[#1A3626]">{inr(fp.fixed_price || fp.price_veg)}</p>
                : <>
                    <p className="font-wvc-display text-[#1A3626]">{inr(fp.price_veg)}<span className="text-xs">/veg</span></p>
                    {Number(fp.price_nonveg) > 0 && <p className="text-xs text-[#1A3626]/60">{inr(fp.price_nonveg)}/non-veg</p>}
                  </>}
            </div>
          </div>
        </OptionCard>
      ))}
      {activePkg && activePkg.pricing_mode === 'per_person' && (
        <div className="border border-[#C6A87C]/50 bg-white p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <CalcLabel>Veg plates</CalcLabel>
            <Stepper value={sel.veg_guests || 0} onChange={v => patch({ veg_guests: v })} min={0} step={10} testId="calc-food-veg" />
          </div>
          <div>
            <CalcLabel>Non-veg plates</CalcLabel>
            <Stepper value={sel.nonveg_guests || 0} onChange={v => patch({ nonveg_guests: v })} min={0} step={10} testId="calc-food-nonveg" />
          </div>
          <div>
            <CalcLabel>Children</CalcLabel>
            <Stepper value={sel.children || 0} onChange={v => patch({ children: v })} min={0} testId="calc-food-children" />
          </div>
          {Number(activePkg.min_guests) > 0 && (
            <p className="text-[11px] text-[#1A3626]/50 sm:col-span-3">Minimum guaranteed count: {activePkg.min_guests} guests.</p>
          )}
        </div>
      )}
      {activePkg && ['per_meal', 'per_day', 'per_event'].includes(activePkg.pricing_mode) && (
        <div className="border border-[#C6A87C]/50 bg-white p-4 sm:p-5">
          <CalcLabel>Number of {activePkg.pricing_mode.replace('per_', '')}s</CalcLabel>
          <Stepper value={sel.units || 1} onChange={v => patch({ units: v })} min={1} testId="calc-food-units" />
        </div>
      )}
    </div>
  );
};

export const StepAddons = ({ config, selections, setSelections, basics }) => {
  const addons = config.addons || [];
  const chosen = selections.addons || [];

  const toggle = (ao) => {
    if (ao.mandatory) return;
    const exists = chosen.find(c => c.addon_id === ao.id);
    setSelections({
      ...selections,
      addons: exists ? chosen.filter(c => c.addon_id !== ao.id) : [...chosen, { addon_id: ao.id, quantity: 1, hours: 1, days: 1, persons: basics.guests || 1 }]
    });
  };
  const patch = (id, changes) => setSelections({
    ...selections,
    addons: chosen.map(c => c.addon_id === id ? { ...c, ...changes } : c)
  });

  const priceLabel = (ao) => {
    const p = inr(ao.price);
    return { fixed: p, per_person: `${p}/person`, per_hour: `${p}/hour`, per_day: `${p}/day`, per_quantity: `${p}/${ao.unit_label || 'unit'}` }[ao.pricing_type] || p;
  };

  return (
    <div className="space-y-3" data-testid="calc-step-addons">
      {addons.map(ao => {
        const sel = chosen.find(c => c.addon_id === ao.id);
        const active = !!sel || ao.mandatory;
        return (
          <div key={ao.id} className={`border bg-white transition-colors ${active ? 'border-[#C6A87C]' : 'border-[#E5DCCF]'}`}>
            <button type="button" onClick={() => toggle(ao)} className="w-full text-left p-4 sm:p-5 flex items-start justify-between gap-3" data-testid={`calc-addon-${ao.id}`}>
              <div>
                <p className="font-wvc-display text-base text-[#0A1710]">
                  {ao.name}
                  {ao.mandatory && <span className="ml-2 text-[9px] uppercase tracking-[0.15em] font-bold bg-[#C6A87C] text-[#0A1710] px-1.5 py-0.5 align-middle">Mandatory</span>}
                </p>
                {ao.description && <p className="text-xs text-[#1A3626]/60 mt-0.5 font-light">{ao.description}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-medium text-[#1A3626]">{priceLabel(ao)}</p>
                <div className={`w-5 h-5 border mt-1.5 ml-auto flex items-center justify-center text-white text-xs ${active ? 'bg-[#1A3626] border-[#1A3626]' : 'border-[#E5DCCF]'}`}>
                  {active && '✓'}
                </div>
              </div>
            </button>
            {sel && ao.pricing_type === 'per_quantity' && (
              <div className="px-4 sm:px-5 pb-4">
                <CalcLabel>Quantity</CalcLabel>
                <Stepper value={sel.quantity || 1} onChange={v => patch(ao.id, { quantity: v })} min={1} step={10} testId={`calc-addon-qty-${ao.id}`} />
              </div>
            )}
            {sel && ao.pricing_type === 'per_hour' && (
              <div className="px-4 sm:px-5 pb-4">
                <CalcLabel>Hours</CalcLabel>
                <Stepper value={sel.hours || 1} onChange={v => patch(ao.id, { hours: v })} min={1} testId={`calc-addon-hours-${ao.id}`} />
              </div>
            )}
            {sel && ao.pricing_type === 'per_day' && (
              <div className="px-4 sm:px-5 pb-4">
                <CalcLabel>Days</CalcLabel>
                <Stepper value={sel.days || 1} onChange={v => patch(ao.id, { days: v })} min={1} testId={`calc-addon-days-${ao.id}`} />
              </div>
            )}
            {sel && ao.pricing_type === 'per_person' && (
              <div className="px-4 sm:px-5 pb-4">
                <CalcLabel>Persons</CalcLabel>
                <Stepper value={sel.persons || 1} onChange={v => patch(ao.id, { persons: v })} min={1} step={10} testId={`calc-addon-persons-${ao.id}`} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
